import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  CreditCard,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { activateSampleEntry } from "./landingHeroCopy";
import {
  CLOSING_CONTROL_LABEL,
  CONTACT_TITLE,
  FAQ_HEADING,
  FAQ_ITEMS,
  PAYMENT_LINES,
  PAYMENT_TITLE,
  REFUND_RULE,
  REFUND_TITLE,
  TRUST_EYEBROW,
  TRUST_HOURS,
  TRUST_TITLE,
  refundPolicyLink,
  toggleFaqIndex,
  trustContactSentence,
} from "./landingTrustCopy";

const CARDS: readonly { key: "refund" | "payment" | "contact"; title: string; icon: LucideIcon }[] =
  [
    { key: "refund", title: REFUND_TITLE, icon: ShieldCheck },
    { key: "payment", title: PAYMENT_TITLE, icon: CreditCard },
    { key: "contact", title: CONTACT_TITLE, icon: MessageCircle },
  ];

export default function LandingTrust() {
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(
    () => new Set<number>([0]),
  );
  const policyLink = refundPolicyLink();
  const contactSentence = trustContactSentence();

  return (
    <section className="ltf" aria-labelledby="ltf-title">
      <style>{styles}</style>
      <div className="ltf-deco-clip" aria-hidden="true">
        <div className="ltf-deco ltf-deco-circle" />
        <div className="ltf-deco ltf-deco-square" />
      </div>

      <header className="ltf-head">
        <p className="ltf-eyebrow">{TRUST_EYEBROW}</p>
        <h2 id="ltf-title" className="ltf-title">
          {TRUST_TITLE}
        </h2>
      </header>

      <div className="ltf-content">
        <div className="ltf-items">
          {CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <article key={card.key} className="ltf-card">
                <div className="ltf-card-top">
                  <span className="ltf-icon" aria-hidden="true">
                    <Icon size={20} strokeWidth={2} />
                  </span>
                  <h3 className="ltf-card-title">{card.title}</h3>
                </div>
                {card.key === "refund" ? (
                  <>
                    <p className="ltf-mint">{REFUND_RULE}</p>
                    {policyLink !== null ? (
                      <Link className="ltf-policy" to={policyLink.to}>
                        {policyLink.label}
                      </Link>
                    ) : null}
                  </>
                ) : null}
                {card.key === "payment"
                  ? PAYMENT_LINES.map((line, lineIndex) => (
                      <p className="ltf-mint" key={lineIndex}>
                        {line}
                      </p>
                    ))
                  : null}
                {card.key === "contact" ? (
                  <>
                    {contactSentence ? <p className="ltf-mint">{contactSentence}</p> : null}
                    <p className="ltf-mint">{TRUST_HOURS}</p>
                  </>
                ) : null}
              </article>
            );
          })}
        </div>

        <section id="duvidas" className="ltf-faq" aria-labelledby="ltf-faq-title">
          <h3 id="ltf-faq-title" className="ltf-faq-title">
            {FAQ_HEADING}
          </h3>
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openIndexes.has(index);
            const panelId = `ltf-panel-${item.id}`;
            const buttonId = `ltf-q-${item.id}`;
            return (
              <div key={item.id} className="ltf-row">
                <button
                  type="button"
                  id={buttonId}
                  className="ltf-q"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() =>
                    setOpenIndexes((current) => toggleFaqIndex(current, index))
                  }
                >
                  <span>{item.question}</span>
                  {isOpen ? (
                    <ChevronUp className="ltf-chevron" size={20} strokeWidth={2} aria-hidden="true" />
                  ) : (
                    <ChevronDown className="ltf-chevron" size={20} strokeWidth={2} aria-hidden="true" />
                  )}
                </button>
                <div
                  id={panelId}
                  className={isOpen ? "ltf-answer" : "ltf-answer ltf-answer-closed"}
                  role="region"
                  aria-labelledby={buttonId}
                  hidden={isOpen ? undefined : true}
                >
                  {item.paragraphs.map((paragraph, paragraphIndex) => (
                    <p className="ltf-mint" key={paragraphIndex}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      </div>

      <button type="button" className="ltf-cta" onClick={activateSampleEntry}>
        {CLOSING_CONTROL_LABEL}
      </button>
    </section>
  );
}

const styles = `
.ltf, .ltf * { box-sizing: border-box; }
.ltf > *,
.ltf-head > *,
.ltf-content > *,
.ltf-items > *,
.ltf-card > *,
.ltf-card-top > *,
.ltf-faq > *,
.ltf-row > *,
.ltf-q > *,
.ltf-answer > * {
  min-width: 0;
}
.ltf {
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
  padding: 56px 16px;
  background: var(--v3l-band-navy-bg);
  color: var(--v3l-band-navy-heading);
  font-family: inherit;
}
.ltf-deco-clip {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}
html.dark .ltf {
  background: var(--v3l-band-navy-bg);
  color: var(--v3l-band-navy-heading);
}
.ltf-deco {
  position: absolute;
  z-index: 0;
  pointer-events: none;
}
.ltf-deco-circle {
  width: 176px;
  height: 176px;
  right: 22px;
  bottom: -113px;
  border-radius: 50%;
  background: var(--v3l-accent-teal);
}
.ltf-deco-square {
  width: 35px;
  height: 35px;
  left: 24px;
  bottom: 24px;
  border-radius: 9px;
  background: var(--accent-amber-on-dark);
}
.ltf-head, .ltf-content, .ltf-cta { position: relative; z-index: 1; }
.ltf-head {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  min-width: 0;
}
.ltf-eyebrow {
  margin: 0;
  color: var(--band-navy-eyebrow);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1.68px;
  line-height: 1.2857;
}
.ltf-title {
  margin: 0;
  color: var(--v3l-band-navy-heading);
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.1333;
}
.ltf-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;
  width: 100%;
  min-width: 0;
}
.ltf-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
}
.ltf-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;
  padding: 24px;
  border: 1px solid var(--v3l-border-on-dark);
  border-radius: 24px;
  background: var(--v3l-surface-navy-2);
  overflow-wrap: break-word;
}
.ltf-card-top {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
}
.ltf-icon {
  display: inline-flex;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--accent-sky-on-dark);
  color: var(--v3l-text-primary);
}
.ltf-card-title, .ltf-faq-title {
  margin: 0;
  color: var(--v3l-band-navy-heading);
  font-weight: 800;
  line-height: 1.2;
}
.ltf-card-title { font-size: 18px; }
.ltf-faq-title { font-size: 22px; }
.ltf-mint {
  margin: 0;
  color: var(--v3l-accent-mint);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4667;
}
.ltf-policy, .ltf-q, .ltf-cta {
  margin: 0;
  font-family: inherit;
  cursor: pointer;
}
.ltf-policy {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--v3l-band-navy-heading);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.ltf-policy:link,
.ltf-policy:visited,
.ltf-policy:hover,
.ltf-policy:active {
  color: var(--v3l-band-navy-heading);
}
.ltf-faq {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-width: 0;
}
.ltf-row {
  min-width: 0;
  border-bottom: 1px solid var(--v3l-band-navy-border);
}
.ltf-q {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  padding: 16px 0;
  border: 0;
  background: transparent;
  color: var(--v3l-band-navy-heading);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
  overflow-wrap: break-word;
}
.ltf-q span { flex: 1; }
.ltf-chevron { flex: 0 0 auto; color: var(--v3l-band-navy-body); }
.ltf-answer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 0 0 16px;
  overflow-wrap: break-word;
}
.ltf-answer.ltf-answer-closed {
  display: none;
}
.ltf-cta {
  align-self: stretch;
  min-width: 0;
  min-height: 44px;
  height: 48px;
  padding: 0 30px;
  border: 0;
  border-radius: 999px;
  background: var(--v3l-ui-cta-dark-bg);
  color: var(--v3l-ui-cta-dark-fg);
  font-size: 17px;
  font-weight: 700;
  line-height: 1;
}
.ltf-cta:hover { background: var(--v3l-ui-cta-dark-bg-hover); }
.ltf-cta:active { box-shadow: inset 0 0 0 2px var(--v3l-ui-cta-dark-pressed-inset-ring); }
.ltf-q:focus-visible,
.ltf-policy:focus-visible,
.ltf-cta:focus-visible {
  outline: 2px solid var(--v3l-ui-cta-dark-focus-ring);
  outline-offset: 4px;
}
@media (min-width: 768px) {
  .ltf {
    gap: 40px;
    padding: 100px clamp(32px, 8.333vw, 120px);
  }
  .ltf-title {
    font-size: 48px;
    letter-spacing: -1.44px;
    line-height: 1.0833;
  }
  .ltf-content {
    flex-direction: row;
    align-items: flex-start;
    gap: 48px;
  }
  .ltf-items {
    flex: 1 1 280px;
    width: auto;
    max-width: 440px;
  }
  .ltf-card { padding: 28px; }
  .ltf-card-title { font-size: 21px; }
  .ltf-faq { flex: 1.4 1 320px; }
  .ltf-cta { align-self: flex-start; width: auto; height: 52px; }
  .ltf-deco-circle {
    width: 320px;
    height: 320px;
    right: 40px;
    bottom: -108px;
  }
  .ltf-deco-square {
    width: 64px;
    height: 64px;
    left: auto;
    right: 260px;
    bottom: 62px;
    border-radius: 16px;
  }
}
`;
